/**
 * Filter sidebar panel for the city map.
 * Shows toggle buttons for each building filter with color indicators,
 * a legend of active filters, and a match count summary.
 */

import {
  FILTERS, activeFilters, toggleFilter, isAnyFilterActive,
  getMatchCount, getMatchSectors,
  type FilterId,
} from './filters';
import { t } from '../i18n';

export interface FilterSidebarCallbacks {
  onToggle(id: FilterId): void;
  onClose(): void;
}

/**
 * Create the filter sidebar element. Caller is responsible for
 * mounting it inside the map area container.
 */
export function createFilterSidebar(callbacks: FilterSidebarCallbacks): HTMLElement {
  const el = document.createElement('div');
  el.className = 'map-filter-sidebar';
  el.style.cssText = `
    position: absolute; left: 0; top: 0; bottom: 0; width: 140px;
    background: rgba(8, 10, 18, 0.92); border-right: 1px solid var(--line);
    z-index: 20; display: flex; flex-direction: column; padding: 10px 8px;
    font-family: "Space Grotesk", system-ui, sans-serif;
    backdrop-filter: blur(6px);
    transition: transform 0.2s ease-out;
  `;

  render(el, callbacks);
  return el;
}

function render(el: HTMLElement, callbacks: FilterSidebarCallbacks): void {
  const anyActive = isAnyFilterActive();

  el.innerHTML = `
    <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:10px">
      <span style="font-size:10px;color:rgba(255,255,255,0.4);text-transform:uppercase;letter-spacing:0.12em;font-weight:600">Filter</span>
      <button class="filter-close-btn" style="background:none;border:none;color:rgba(255,255,255,0.3);cursor:pointer;font-size:14px;padding:0 2px;line-height:1" title="${t('filter.close')}">&times;</button>
    </div>
    <div class="filter-list" style="flex:1;overflow-y:auto">
      ${FILTERS.map(f => {
        const active = activeFilters.has(f.id);
        return `<button class="filter-toggle${active ? ' active' : ''}" data-filter="${f.id}" style="
          display:flex;align-items:center;gap:6px;width:100%;padding:5px 6px;margin-bottom:2px;
          background:${active ? 'rgba(255,255,255,0.06)' : 'transparent'};
          border:1px solid ${active ? f.color + '66' : 'transparent'};
          border-radius:3px;cursor:pointer;transition:all 0.15s;
          font-family:inherit;text-align:left;
        ">
          <span style="width:10px;height:10px;border-radius:2px;flex-shrink:0;
            background:${active ? f.color : 'rgba(255,255,255,0.12)'};
            box-shadow:${active ? '0 0 6px ' + f.color + '88' : 'none'};
            transition:all 0.15s;
          "></span>
          <span style="font-size:11px;color:${active ? '#e0e8f0' : '#5a6a7a'};transition:color 0.15s">${f.label}</span>
        </button>`;
      }).join('')}
    </div>
    ${anyActive ? `
      <div style="margin-top:8px;padding-top:8px;border-top:1px solid rgba(255,255,255,0.06)">
        <div style="font-size:9px;color:rgba(255,255,255,0.3);text-transform:uppercase;letter-spacing:0.1em;margin-bottom:4px">${t('filter.matches')}</div>
        <div style="font-size:13px;color:#e0e8f0;font-family:monospace;font-weight:600">${getMatchCount()}</div>
        <div style="font-size:10px;color:#5a6a7a">${t('filter.in_sectors', { count: getMatchSectors() })}</div>
      </div>
      <div style="margin-top:8px;padding-top:6px;border-top:1px solid rgba(255,255,255,0.06)">
        <div style="font-size:9px;color:rgba(255,255,255,0.3);text-transform:uppercase;letter-spacing:0.1em;margin-bottom:4px">${t('filter.legend')}</div>
        ${FILTERS.filter(f => activeFilters.has(f.id)).map(f => `
          <div style="display:flex;align-items:center;gap:5px;margin-bottom:2px">
            <span style="width:8px;height:8px;border-radius:1px;background:${f.color};box-shadow:0 0 4px ${f.color}88"></span>
            <span style="font-size:10px;color:#8a9aaa">${f.label}</span>
          </div>
        `).join('')}
        <div style="display:flex;align-items:center;gap:5px;margin-top:3px;opacity:0.5">
          <span style="width:8px;height:8px;border-radius:1px;background:rgba(255,255,255,0.08)"></span>
          <span style="font-size:10px;color:#5a6a7a">${t('filter.dimmed')}</span>
        </div>
      </div>
    ` : ''}
  `;

  // Wire up events
  el.querySelector('.filter-close-btn')?.addEventListener('click', callbacks.onClose);

  el.querySelectorAll('.filter-toggle').forEach(btn => {
    btn.addEventListener('click', () => {
      const id = (btn as HTMLElement).dataset.filter as FilterId;
      toggleFilter(id);
      callbacks.onToggle(id);
      render(el, callbacks); // re-render sidebar
    });
    btn.addEventListener('mouseenter', () => {
      if (!activeFilters.has((btn as HTMLElement).dataset.filter as FilterId)) {
        (btn as HTMLElement).style.background = 'rgba(255,255,255,0.03)';
      }
    });
    btn.addEventListener('mouseleave', () => {
      if (!activeFilters.has((btn as HTMLElement).dataset.filter as FilterId)) {
        (btn as HTMLElement).style.background = 'transparent';
      }
    });
  });
}

/** Update the match count display without full re-render */
export function updateFilterSidebar(el: HTMLElement, callbacks: FilterSidebarCallbacks): void {
  render(el, callbacks);
}
